import { LucideIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { cn } from '@/lib/utils';

const segmentClass = 'flex items-center gap-1 bg-muted/50 px-1.5 py-1 text-sm leading-5';

const focusClass =
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset';

const variantClass = {
  comparison: 'min-w-7 shrink-0 justify-center font-medium text-muted-foreground',
  value: 'min-w-0 font-medium text-foreground',
};

const TruncatedSegmentText = ({ children }: { children: React.ReactNode }) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [children]);

  return (
    <InfoTooltip content={isTruncated ? children : undefined} size="auto">
      <span ref={ref} className="truncate">
        {children}
      </span>
    </InfoTooltip>
  );
};

type SegmentedChipProps = {
  children: React.ReactNode;
  error?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export const SegmentedChip = ({ children, error = false, ref }: SegmentedChipProps) => (
  <div
    ref={ref}
    className={cn(
      'flex max-w-full min-w-0 items-stretch overflow-hidden rounded-md border border-border bg-background',
      error && 'border-warning'
    )}
  >
    {children}
  </div>
);

type SegmentedChipLabelProps = { children: string; icon?: LucideIcon };

export const SegmentedChipLabel = ({ children, icon: Icon }: SegmentedChipLabelProps) => (
  <span className={cn(segmentClass, 'min-w-0 font-normal text-foreground')}>
    {Icon && <Icon className="size-4 shrink-0 text-muted-foreground" />}
    <TruncatedSegmentText>{children}</TruncatedSegmentText>
  </span>
);

type SegmentedChipSegmentProps = React.ComponentProps<'button'> & {
  variant: keyof typeof variantClass;
};

export const SegmentedChipSegment = ({
  variant,
  disabled = false,
  className,
  children,
  ...props
}: SegmentedChipSegmentProps) => {
  const classes = cn(segmentClass, 'border-l border-border', variantClass[variant], className);
  const content =
    variant === 'value' ? <TruncatedSegmentText>{children}</TruncatedSegmentText> : children;

  if (disabled) {
    return <span className={classes}>{content}</span>;
  }

  return (
    <button
      type="button"
      className={cn(classes, focusClass, 'text-left hover:bg-selected-soft')}
      {...props}
    >
      {content}
    </button>
  );
};

type SegmentedChipPlaceholderProps = { children: string };

export const SegmentedChipPlaceholder = ({ children }: SegmentedChipPlaceholderProps) => (
  <span className="pr-0.5 font-normal text-muted-foreground italic">{children}</span>
);

type SegmentedChipRemoveProps = { label: string; onClick: () => void };

export const SegmentedChipRemove = ({ label, onClick }: SegmentedChipRemoveProps) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-label={t('common.remove_value', { 0: label })}
      className={cn(
        'flex shrink-0 items-center border-l border-border bg-muted/50 px-1.5 text-muted-foreground hover:bg-selected-soft',
        focusClass
      )}
      onClick={onClick}
    >
      <XIcon className="size-4" />
    </button>
  );
};
